import { useState } from 'react';
import { User, Lock, Save } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../utils/api';
import toast from 'react-hot-toast';

export default function Profile() {
  const { user, login } = useAuth();
  const [tab, setTab] = useState('profile');
  const [form, setForm] = useState({ name: user?.name || '', phone: user?.phone || '', address: user?.address || { street: '', city: '', state: '', zipCode: '', country: 'Tunisia' } });
  const [pwdForm, setPwdForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [loading, setLoading] = useState(false);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await authAPI.updateProfile(form);
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
    setLoading(false);
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.newPassword !== pwdForm.confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await authAPI.updatePassword({ currentPassword: pwdForm.currentPassword, newPassword: pwdForm.newPassword });
      toast.success('Password updated!');
      setPwdForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Update failed');
    }
    setLoading(false);
  };

  return (
    <div className="container" style={{ padding: '40px 24px', maxWidth: 800 }}>
      <h1 style={{ fontFamily: 'Syne', fontSize: 36, fontWeight: 800, marginBottom: 8 }}>My Profile</h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: 32 }}>Manage your account settings</p>

      {/* Avatar card */}
      <div className="card" style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 24 }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 800, flexShrink: 0 }}>
          {user?.name[0].toUpperCase()}
        </div>
        <div>
          <h2 style={{ fontFamily: 'Syne', fontSize: 22, fontWeight: 700 }}>{user?.name}</h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{user?.email}</p>
          <span style={{ fontSize: 12, background: user?.role === 'admin' ? 'rgba(99,102,241,0.15)' : 'rgba(16,185,129,0.15)', color: user?.role === 'admin' ? 'var(--accent)' : 'var(--success)', padding: '3px 10px', borderRadius: 8, fontWeight: 600 }}>{user?.role}</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid var(--border)', marginBottom: 32 }}>
        {[['profile', User, 'Profile Info'], ['password', Lock, 'Password']].map(([t, Icon, label]) => (
          <button key={t} onClick={() => setTab(t)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px', background: 'none', border: 'none', borderBottom: tab === t ? '2px solid var(--accent)' : '2px solid transparent', color: tab === t ? 'var(--accent)' : 'var(--text-secondary)', cursor: 'pointer', fontFamily: 'Syne', fontWeight: 600, fontSize: 14, marginBottom: -1 }}>
            <Icon size={16} />{label}
          </button>
        ))}
      </div>

      {tab === 'profile' && (
        <div className="card">
          <form onSubmit={handleUpdateProfile}>
            <div className="form-row-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Full Name</label>
                <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Phone</label>
                <input className="form-input" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+216 xx xxx xxx" />
              </div>
              <div className="form-group" style={{ gridColumn: '1/-1' }}>
                <label className="form-label">Street Address</label>
                <input className="form-input" value={form.address.street} onChange={e => setForm(p => ({ ...p, address: { ...p.address, street: e.target.value } }))} />
              </div>
              {[['City', 'city'], ['State', 'state'], ['Zip Code', 'zipCode'], ['Country', 'country']].map(([label, key]) => (
                <div key={key} className="form-group">
                  <label className="form-label">{label}</label>
                  <input className="form-input" value={form.address[key] || ''} onChange={e => setForm(p => ({ ...p, address: { ...p.address, [key]: e.target.value } }))} />
                </div>
              ))}
            </div>
            <button className="btn btn-primary" type="submit" disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Save size={16} />{loading ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      )}

      {tab === 'password' && (
        <div className="card">
          <form onSubmit={handleUpdatePassword}>
            {[['Current Password', 'currentPassword'], ['New Password', 'newPassword'], ['Confirm New Password', 'confirm']].map(([label, key]) => (
              <div key={key} className="form-group">
                <label className="form-label">{label}</label>
                <input className="form-input" type="password" value={pwdForm[key]} onChange={e => setPwdForm(p => ({ ...p, [key]: e.target.value }))} required />
              </div>
            ))}
            <button className="btn btn-primary" type="submit" disabled={loading}>{loading ? 'Updating...' : 'Update Password'}</button>
          </form>
        </div>
      )}
    </div>
  );
}
